import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ANIMAL_TYPES } from 'src/app/add-customer/helpers/constants';
import { Languages } from '../../translation/languages.enum';
import { localeDe, localeFr, localeIt } from '@mobiscroll/angular';
import { CustomTranslationService } from '../../translation/custom-translation.service';
import { AdrAnimalDto, AdrAnimalDateType, AdrAnimalExternalNr, AnimalDateType, AnimalExternalNumber, AnimalType, Translation } from 'src/app/api/models';
import { select, Store } from '@ngrx/store';
import * as fromStaticDataSelectors from 'src/app/store/selectors/static-data.selector';
import * as fromStaticDataReasonActions from 'src/app/store/actions/static-data.action';
import { untilComponentDestroyed } from '../../helpers/rxjs.operator';
import { AppState } from 'src/app/store/states/app.state';
import { DateTimeHelper } from '../../helpers/datetime.helper';
import { GuidHelper } from '../../helpers/guid.helper';
@Component({
  selector: 'diana-animal-details',
  standalone: false,
  templateUrl: './animal-details.component.html',
  styleUrl: './animal-details.component.scss',
})
export class AnimalDetailsComponent implements OnInit, OnChanges {
  @Input() animal!: AdrAnimalDto;
  @Input() isMultipleAnimals = true;
  @Input() showGeneralData = true;
  @Input() isFormInputsDisabled = false;
  @Output() newChanges = new EventEmitter<any>();
  @Output() changeActiveStatus = new EventEmitter<boolean>();

  get animalsFormArray(): FormArray {
    return this.formGroup.get('animals') as FormArray;
  }
  public formGroup!: FormGroup;
  public animalTypeNames = ANIMAL_TYPES;
  public maxBirthDate = DateTimeHelper.now();
  public currentLanguage: Languages = Languages.de;
  private translations: Translation[] = [];
  public localeDatePicker = localeDe;
  public animalTypes: Array<AnimalType> = [];
  public externalNumbersAvailable: AnimalExternalNumber[] = [];
  public dateTypesAvailable: AnimalDateType[] = [];

  getAnimalDatesFormArray(index: number): any {
    return this.animalsFormArray.at(index).get('dates') as any;
  }

  getAnimalExternalNumbersFormArray(index: number): any {
    return this.animalsFormArray.at(index).get('externalNumbers') as any;
  }
  constructor(
    private fb: FormBuilder,
    private translateSrv: CustomTranslationService,
    private store$: Store<AppState>,
  ) {
    this.currentLanguage = (this.translateSrv.currentLang as Languages)
    switch (this.translateSrv.currentLang) {
      case Languages.de: this.localeDatePicker = localeDe;
        break;
      case Languages.fr: this.localeDatePicker = localeFr;
        break;
      case Languages.it: this.localeDatePicker = localeIt;
        break;
    }
    this.getAnimalTypeTranslations();
    this.formGroup = this.fb.group({
      animals: this.fb.array([])
    });
  }
  ngOnInit(): void {
    this.getAnimalTypes();
    this.addFormNewAnimal();
    this.handleDisableMicrochipDate();
    if (!this.isMultipleAnimals) {
      this.store$.dispatch(fromStaticDataReasonActions.initAnimalExternalNumbersData());
      this.getAnimalExternalNumbers();

      this.store$.dispatch(fromStaticDataReasonActions.initAnimalDateTypesData());
      this.getAnimalDateTypes();
    }
  }

  public onChangeActiveStatus(): void {
    this.changeActiveStatus.emit();
  }

  public addFormNewDateType(animalIndex: number): void {
    const dates = {
      id: GuidHelper.newGuid(),
      dateType: new FormControl(undefined, Validators.required),
      date: new FormControl(undefined, Validators.required),
    }
    const datesFormArray = this.getAnimalDatesFormArray(animalIndex);
    datesFormArray.insert(0, this.fb.group(dates));
  }

  public insertDateType(animalIndex: number, existingDateType: AdrAnimalDateType): void {
    const dateType = this.dateTypesAvailable.find(date => date.id === existingDateType.dateTypeId);
    const newDateType = {
      id: new FormControl(existingDateType.id),
      dateType: new FormControl({ value: dateType, disabled: this.isFormInputsDisabled }, Validators.required),
      date: new FormControl({ value: DateTimeHelper.copyDate(existingDateType.value), disabled: this.isFormInputsDisabled }, Validators.required),
    }
    const dateTypeFormArray = this.getAnimalDatesFormArray(animalIndex);
    dateTypeFormArray.push(this.fb.group(newDateType));
  }

  public addFormNewExternalNumber(animalIndex: number): void {
    const externalNumbers = {
      id: new FormControl(GuidHelper.newGuid()),
      numberType: new FormControl(undefined, Validators.required),
      number: new FormControl(undefined, Validators.required),
    }
    const externalNumbersFormArray = this.getAnimalExternalNumbersFormArray(animalIndex);
    externalNumbersFormArray.insert(0, this.fb.group(externalNumbers));
  }

  public insertExternalNumber(animalIndex: number, existingExternalNumber: AdrAnimalExternalNr): void {
    const numberType = this.externalNumbersAvailable.find(number => number.id === existingExternalNumber.externalNumberId);

    const externalNumbers = {
      id: new FormControl(existingExternalNumber.id),
      numberType: new FormControl({ value: numberType, disabled: this.isFormInputsDisabled }, Validators.required),
      number: new FormControl({ value: existingExternalNumber.value, disabled: this.isFormInputsDisabled }, Validators.required),
    }
    if (numberType && numberType.name === 'MicroChip') {
      externalNumbers.number.setValidators([Validators.maxLength(15), Validators.minLength(15)])
    }
    const externalNumbersFormArray = this.getAnimalExternalNumbersFormArray(animalIndex);
    externalNumbersFormArray.push(this.fb.group(externalNumbers));
  }

  private getAnimalExternalNumbers(): void {
    this.store$
      .pipe(
        select(fromStaticDataSelectors.getAnimalExternalNumbers()),
        untilComponentDestroyed(this)
      )
      .subscribe((animalExternalNumbers: Array<AnimalExternalNumber>) => {
        this.externalNumbersAvailable = [...animalExternalNumbers];
        if (this.externalNumbersAvailable.length > 0) {
          if (this.animal) {
            this.animal?.externalNrs?.map(number => {
              this.insertExternalNumber(0, number);
            });
          }
        }
      });
  }
  private getAnimalDateTypes(): void {
    this.store$
      .pipe(
        select(fromStaticDataSelectors.getAnimalDateTypes()),
        untilComponentDestroyed(this)
      )
      .subscribe((animalDateTypes: Array<AnimalDateType>) => {
        this.dateTypesAvailable = [...animalDateTypes];

        if (this.dateTypesAvailable.length > 0) {
          if (this.animal) {
            this.animal?.dateTypes?.map(date => {
              this.insertDateType(0, date);
            });
          }
        }
      });
  }

  public deleteAnimal(index: number): void {
    this.animalsFormArray.removeAt(index);
    this.animalsFormArray.markAsDirty();
  }

  public addFormNewAnimal(): void {
    const formAnimal = {
      animalName: new FormControl({ value: this.animal?.name || '', disabled: this.isFormInputsDisabled }, Validators.required),
      catteryName: new FormControl({ value: this.animal?.catteryName || '', disabled: this.isFormInputsDisabled }),
      species: new FormControl({ value: this.animal?.species || '', disabled: this.isFormInputsDisabled }, Validators.required),
      race: new FormControl({ value: this.animal?.race || '', disabled: this.isFormInputsDisabled }, Validators.required),
      animalColor: new FormControl({ value: this.animal?.color || '', disabled: this.isFormInputsDisabled }, Validators.required),
      sex: new FormControl({ value: this.animal?.sex || '', disabled: this.isFormInputsDisabled }, Validators.required),
      isCastrated: new FormControl({ value: this.animal?.isCastrated ?? false, disabled: this.isFormInputsDisabled }),
      birthDate: new FormControl({ value: null, disabled: this.isFormInputsDisabled }, Validators.required),
      microChip: new FormControl({ value: undefined, disabled: this.isFormInputsDisabled }, [Validators.maxLength(15), Validators.minLength(15)]),
      microChipDate: new FormControl({ value: undefined, disabled: true }),
      dates: this.fb.array([]),
      externalNumbers: this.fb.array([]),
    }

    if (!this.isMultipleAnimals) {
      formAnimal.birthDate.setValidators([]);
      formAnimal.microChip.setValidators([]);
      formAnimal.microChipDate.setValidators([]);
    }
    this.animalsFormArray.insert(0, this.fb.group(formAnimal));
  }

  private disableInputs(): void {
    if (this.isFormInputsDisabled) {
      this.animalsFormArray.at(0).get('animalName')?.disable();
      this.animalsFormArray.at(0).get('catteryName')?.disable();
      this.animalsFormArray.at(0).get('species')?.disable();
      this.animalsFormArray.at(0).get('race')?.disable();
      this.animalsFormArray.at(0).get('animalColor')?.disable();
      this.animalsFormArray.at(0).get('sex')?.disable();
      this.animalsFormArray.at(0).get('isCastrated')?.disable();
      this.animalsFormArray.at(0).get('externalNumbers')?.disable();
      this.animalsFormArray.at(0).get('dates')?.disable();
      const externalNumbersFormArray = this.getAnimalExternalNumbersFormArray(0);
      externalNumbersFormArray.controls.map((externalNumber: FormGroup) => {
        externalNumber.get('numberType')?.disable();
        externalNumber.get('number')?.disable();
      });
      const dateTypeFormArray = this.getAnimalDatesFormArray(0);
      dateTypeFormArray.controls.map((dateTypeFormGroup: FormGroup) => {
        dateTypeFormGroup.get('dateType')?.disable();
        dateTypeFormGroup.get('date')?.disable();
      });
    } else {
      this.animalsFormArray.at(0).get('animalName')?.enable();
      this.animalsFormArray.at(0).get('catteryName')?.enable();
      this.animalsFormArray.at(0).get('species')?.enable();
      this.animalsFormArray.at(0).get('race')?.enable();
      this.animalsFormArray.at(0).get('animalColor')?.enable();
      this.animalsFormArray.at(0).get('sex')?.enable();
      this.animalsFormArray.at(0).get('isCastrated')?.enable();
      this.animalsFormArray.at(0).get('externalNumbers')?.enable();
      this.animalsFormArray.at(0).get('dates')?.enable();
      const externalNumbersFormArray = this.getAnimalExternalNumbersFormArray(0);
      externalNumbersFormArray.controls.map((externalNumber: FormGroup) => {
        externalNumber.get('numberType')?.enable();
        externalNumber.get('number')?.enable();
      });
      const dateTypeFormArray = this.getAnimalDatesFormArray(0);
      dateTypeFormArray.controls.map((dateTypeFormGroup: FormGroup) => {
        dateTypeFormGroup.get('dateType')?.enable();
        dateTypeFormGroup.get('date')?.enable();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.disableEnableFormInputs();
    if (changes.isFormInputsDisabled && changes.isFormInputsDisabled.firstChange === false) {
      this.disableInputs();
    }
  }

  private disableEnableFormInputs(): void {
    if (this.animalsFormArray.length > 0) {
      this.animalsFormArray.controls.map((animal) => {
        if (this.isFormInputsDisabled) {
          animal.get('sex')?.disable();
          animal.get('castrated')?.disable();
        } else {
          animal.get('sex')?.enable();
          animal.get('castrated')?.enable();
        }
      });
    }
  }

  private getAnimalTypeTranslations(): void {
    this.animalTypeNames = [];
    let keyProps = {
      keys: ANIMAL_TYPES.map(key => 'addNewCustomer.animalType.dropdown.option.' + key)
    };
    // Generating the list animal species already translated
    keyProps.keys.map(key => {
      this.animalTypeNames.push(this.translateSrv.instant(key));
    });
    this.animalTypeNames.sort();
    this.store$
      .pipe(
        select(fromStaticDataSelectors.translationsByKeys(keyProps)),
        untilComponentDestroyed(this)
      )
      .subscribe((translations) => {
        if (translations.length < 2) {
          this.store$.dispatch(
            fromStaticDataReasonActions.loadTranslations(keyProps)
          );
        }
        this.translations = translations;
      });
  }

  private handleDisableMicrochipDate(): void {
    this.animalsFormArray.valueChanges
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        if (this.animalsFormArray.controls.length > 0) {
          this.animalsFormArray.controls.map((animal) => {
            if (animal.get('microChip')?.value && animal.get('microChipDate')?.disabled) {
              animal.get('microChipDate')?.enable({ emitEvent: false });
            } else if (!animal.get('microChip')?.value && animal.get('microChipDate')?.enabled) {
              animal.get('microChipDate')?.disable({ emitEvent: false });
            }
          });
          this.newChanges.emit(this.animalsFormArray);
        }
      });
  }

  private getAnimalTypes(): void {
    this.store$
      .pipe(
        select(fromStaticDataSelectors.isLoadedAnimalTypes),
        untilComponentDestroyed(this)
      )
      .subscribe((isLoadedAnimalTypes: boolean) => {
        if (!isLoadedAnimalTypes) {
          this.store$.dispatch(
            fromStaticDataReasonActions.loadAnimalTypes()
          );
        } else {
          this.store$
            .pipe(
              select(fromStaticDataSelectors.getAnimalTypes()),
              untilComponentDestroyed(this)
            )
            .subscribe((animalTypes: Array<AnimalType>) => {
              this.animalTypes = [...animalTypes];
              this.animalTypes.sort((a, b) => {
                if (a.bid && b.bid) {
                  return a.bid - b.bid;
                }
                return 0;
              });
            });
        }
      });
  }

  public deleteDateType(animalIndex: number, index: number): void {
    const datesFormArray = this.getAnimalDatesFormArray(animalIndex);

    datesFormArray.removeAt(index);
    datesFormArray.markAsDirty();
  }
  public deleteExternalNumberType(animalIndex: number, index: number): void {
    const externalNumbersFormArray = this.getAnimalExternalNumbersFormArray(animalIndex);

    externalNumbersFormArray.removeAt(index);
    externalNumbersFormArray.markAsDirty();
  }

  public convertToFormGroup(element: any): FormGroup {
    return element as FormGroup;
  }

}
